/**
 * PipelineContext · selector global Founders / Ambassadors / Todos.
 * Estado persistido en localStorage para que sobreviva refresh.
 *
 * Cualquier pantalla que filtra por pipeline lee `usePipeline()` y
 * pasa `?pipeline=<slug>` al backend.
 */
import { createContext, useContext, useEffect, useState } from "react";
import api from "./api";
import { useAuth } from "./auth";

export const ALL_PIPELINE = { slug: "all", label: "Todos los pipelines", short: "Todos", color: "carbon" };
export const PIPELINES = [
  ALL_PIPELINE,
  { slug: "founder", label: "Founders Pipeline", short: "Founders", color: "terracotta" },
  { slug: "ambassador", label: "Ambassadors Pipeline", short: "Ambassadors", color: "forest" },
];

const STORAGE_KEY = "hm.activePipelineSlug";

const PipelineContext = createContext({
  activePipelineSlug: "all",
  setActivePipelineSlug: () => {},
  activePipeline: PIPELINES[0],
});

export function PipelineProvider({ children }) {
  const { user } = useAuth();
  const [pipelines, setPipelines] = useState(PIPELINES);
  const [activePipelineSlug, setActivePipelineSlugState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return stored;
    } catch (_e) { /* localStorage may be unavailable */ }
    return "all";
  });

  // Re-fetch pipelines whenever the user becomes authenticated so newly
  // created pipelines always appear in the switcher (avoids 401 on login page).
  useEffect(() => {
    if (!user) return;
    api.get("/pipelines").then(({ data }) => {
      const items = Array.isArray(data) ? data : (data?.items || []);
      const dynamic = items.map((p) => ({
        slug: p.slug,
        label: p.name || p.slug,
        short: p.name || p.slug,
        color: p.slug === "founder" ? "terracotta" : (p.target_role === "ambassador" || p.slug === "ambassador") ? "forest" : "carbon",
        status: p.status,
      }));
      if (dynamic.length) setPipelines([ALL_PIPELINE, ...dynamic]);
    }).catch(() => { /* keep static fallback */ });
  }, [user]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, activePipelineSlug); } catch (_e) { /* ignore */ }
  }, [activePipelineSlug]);

  const setActivePipelineSlug = (slug) => setActivePipelineSlugState(slug);

  const activePipeline = pipelines.find((p) => p.slug === activePipelineSlug) || pipelines[0];

  return (
    <PipelineContext.Provider value={{ pipelines, activePipelineSlug, setActivePipelineSlug, activePipeline }}>
      {children}
    </PipelineContext.Provider>
  );
}

export function usePipeline() {
  return useContext(PipelineContext);
}

/** Helper: appends `pipeline=` param to API requests when filter active.
 *  Use as: `api.get("/users", { params: pipelineParams(activePipelineSlug, { limit: 100 }) })`
 */
export function pipelineParams(slug, extra = {}) {
  if (!slug || slug === "all") return extra;
  return { ...extra, pipeline: slug };
}
